import { useEffect, useState } from 'react'
import { club, type ClubQuestionnaire } from '../../api/club'
import { ApiError } from '../../api/client'

interface InvitationsModalProps {
  clubId: number
  onClose: () => void
}

export function InvitationsModal({ clubId, onClose }: InvitationsModalProps) {
  const [emails, setEmails] = useState('')
  const [questionnaires, setQuestionnaires] = useState<ClubQuestionnaire[]>([])
  const [selectedCode, setSelectedCode] = useState<string>('')
  const [timepoint, setTimepoint] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ created: number; reused: number; failed: Array<{ email: string; reason: string }> } | null>(null)

  useEffect(() => {
    const load = async () => {
      setLoadingQuestionnaires(true)
      try {
        const data = await club.questionnaires(clubId)
        setQuestionnaires(data.items)
        if (data.items.length > 0) {
          setSelectedCode(data.items[0]!.code)
        }
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Error al cargar cuestionarios.')
      } finally {
        setLoadingQuestionnaires(false)
      }
    }
    void load()
  }, [clubId])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!emails.trim()) {
      setError('Ingresá al menos un email.')
      return
    }
    if (!selectedCode) {
      setError('Seleccioná un cuestionario.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const emailList = emails
        .split(/[\n,]/)
        .map((e) => e.trim())
        .filter((e) => e.length > 0)
      const res = await club.invite(clubId, {
        emails: emailList,
        code: selectedCode,
        timepoint: timepoint || undefined,
      })
      setResult(res)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al enviar invitaciones.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-md rounded-t-lg bg-surface p-lg shadow-lg sm:rounded-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-text-primary mb-md">Enviar invitaciones</h2>

        {result ? (
          <div className="space-y-md">
            <div className="rounded-sm border border-success/30 bg-success/10 px-md py-sm text-sm">
              <p className="font-semibold text-text-primary">Invitaciones procesadas</p>
              <p className="text-sm text-text-secondary mt-xs">Creadas: {result.created}</p>
              {result.reused > 0 && <p className="text-sm text-text-secondary">Reutilizadas: {result.reused}</p>}
              {result.failed.length > 0 && (
                <div className="mt-sm">
                  <p className="text-sm text-danger">Errores ({result.failed.length}):</p>
                  <ul className="text-xs text-danger mt-xs list-disc list-inside">
                    {result.failed.slice(0, 5).map((f) => (
                      <li key={f.email}>
                        {f.email}: {f.reason}
                      </li>
                    ))}
                    {result.failed.length > 5 && <li>... y {result.failed.length - 5} más</li>}
                  </ul>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn-primary w-full"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-md">
            {error && <div className="banner-err">{error}</div>}

            {loadingQuestionnaires ? (
              <p className="text-sm text-text-muted">Cargando cuestionarios…</p>
            ) : (
              <>
                <label className="flex flex-col gap-xs text-sm">
                  Cuestionario *
                  <select
                    className="input"
                    required
                    value={selectedCode}
                    onChange={(e) => setSelectedCode(e.target.value)}
                    disabled={loading}
                  >
                    {questionnaires.map((q) => (
                      <option key={q.code} value={q.code}>
                        {q.name_es}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-xs text-sm">
                  Timepoint (opcional)
                  <input
                    type="text"
                    className="input"
                    placeholder="ej: unica, T1, T3"
                    value={timepoint}
                    onChange={(e) => setTimepoint(e.target.value)}
                    disabled={loading}
                  />
                </label>

                <label className="flex flex-col gap-xs text-sm">
                  Emails (uno por línea o separados por comas) *
                  <textarea
                    className="input min-h-[120px] font-mono text-xs"
                    required
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                    disabled={loading}
                    placeholder="jugador1@example.com&#10;jugador2@example.com"
                  />
                </label>

                <div className="flex gap-sm pt-md">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-ghost flex-1"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                    disabled={loading}
                  >
                    {loading ? 'Enviando…' : 'Enviar invitaciones'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
